import { useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "sonner";
import {
  createAbsence,
  createWeeklyAvailability,
  deleteAbsence,
  deleteWeeklyAvailability,
  previewAvailabilityImpact,
  updateAbsence,
  updateWeeklyAvailability,
} from "services/availability";
import { ImpactedAppointment } from "services/availability/types";
import { AvailabilityChange, impactRequestFor } from "utils/availability";
import { DOCTOR_AVAILABILITY_KEY, DOCTOR_SLOTS_KEY } from "../queries/useDoctorAvailability";

const SUCCESS_MESSAGES: Record<AvailabilityChange["kind"], string> = {
  createWeekly: "Plage ajoutée",
  updateWeekly: "Plage modifiée",
  deleteWeekly: "Plage supprimée",
  createAbsence: "Absence ajoutée",
  updateAbsence: "Absence modifiée",
  deleteAbsence: "Absence supprimée",
};

const executeChange = (doctorId: number, change: AvailabilityChange, confirm: boolean) => {
  switch (change.kind) {
    case "createWeekly":
      return createWeeklyAvailability(doctorId, change.request, confirm);
    case "updateWeekly":
      return updateWeeklyAvailability(change.weeklyId, change.request, confirm);
    case "deleteWeekly":
      return deleteWeeklyAvailability(change.weeklyId, confirm);
    case "createAbsence":
      return createAbsence(doctorId, change.request, confirm);
    case "updateAbsence":
      return updateAbsence(change.absenceId, change.request, confirm);
    case "deleteAbsence":
      return deleteAbsence(change.absenceId);
  }
};

/** Changement en attente de confirmation : il touche des RDV futurs. */
export type PendingAvailabilityChange = {
  change: AvailabilityChange;
  appointments: ImpactedAppointment[];
};

/**
 * Applique un changement de disponibilité en deux temps (D5) : aperçu des RDV
 * futurs touchés, puis mutation. Sans RDV touché, la mutation part directement ;
 * sinon le changement reste en attente (`pending`) jusqu'à `confirmPending`
 * (mutation avec `confirm=true`, les RDV restent en place) ou `cancelPending`.
 * Les erreurs serveur sont déjà toastées par `apiRequest`.
 */
export function useAvailabilityChange(doctorId: number | null | undefined, onApplied: () => void) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingAvailabilityChange | null>(null);
  const [isWorking, setIsWorking] = useState(false);

  const apply = async (change: AvailabilityChange, confirm: boolean) => {
    const result: any = await executeChange(doctorId as number, change, confirm);
    if (!result?.ok) return;
    toast.success(SUCCESS_MESSAGES[change.kind]);
    setPending(null);
    queryClient.invalidateQueries([DOCTOR_AVAILABILITY_KEY, doctorId]);
    queryClient.invalidateQueries([DOCTOR_SLOTS_KEY, doctorId]);
    onApplied();
  };

  const runWhileWorking = async (task: () => Promise<void>) => {
    setIsWorking(true);
    try {
      await task();
    } finally {
      setIsWorking(false);
    }
  };

  const request = (change: AvailabilityChange) =>
    runWhileWorking(async () => {
      if (doctorId == null) return;
      const impactRequest = impactRequestFor(change);
      if (impactRequest) {
        const impact: any = await previewAvailabilityImpact(doctorId, impactRequest);
        if (!impact?.ok) return;
        const appointments: ImpactedAppointment[] = Array.isArray(impact.data) ? impact.data : [];
        if (appointments.length > 0) {
          setPending({ change, appointments });
          return;
        }
      }
      await apply(change, false);
    });

  const confirmPending = () =>
    runWhileWorking(async () => {
      if (pending) await apply(pending.change, true);
    });

  const cancelPending = () => setPending(null);

  return { request, pending, confirmPending, cancelPending, isWorking };
}

export default useAvailabilityChange;
